import { Component, Host, h, Prop, State, Method, Listen, Element, Watch, Event, EventEmitter } from "@stencil/core"
import { SnapSelectMenuOption } from "../../utils"

@Component({
  tag: 'snap-select-menu',
  styleUrl: 'snap-select-menu.scss',
  shadow: true,
})
export class SnapSelectMenu {

  @Prop({ reflect: true }) error: boolean = false
  @Prop({ reflect: true }) disabled: boolean = false
  @Prop({ reflect: true }) locked: boolean = false
  @Prop({ reflect: true }) relative: boolean = false
  @Prop({ reflect: true }) search: boolean = false
  @Prop({ reflect: true }) multi: boolean = false
  @Prop({ reflect: true }) helpText: string = ''
  @Prop({ reflect: true }) label: string = ''
  @Prop({ reflect: true }) cornerHint: string = ''
  @Prop({ reflect: true }) placeholder: string = ''
  @Prop() selectMenuOptions: SnapSelectMenuOption[] = []
  @Prop({ reflect: true }) modalTitle: string = 'Title'
  @Prop({ reflect: true }) modalType: 'fullscreen' | 'drawer' = 'fullscreen'
  @Prop({ reflect: true }) searchMethod: 'start' | 'contain' = 'start'
  @Prop({ reflect: true }) required: boolean = false
  @Prop({ reflect: true }) float: boolean = false
  @Prop({ reflect: true }) dropdownHeight: string = ''

  @Element() private element: HTMLElement
  @Event({ eventName: 'snap-select-menu-updated', bubbles: true, composed: true }) snapSelectMenuUpdated: EventEmitter<SnapSelectMenuOption[]>
  @Event({ eventName: 'snap-select-menu-scroll-updated', bubbles: true, composed: true }) snapSelectMenuScrollUpdated: EventEmitter<boolean>
  @Event({ eventName: 'snap-select-menu-search-updated', bubbles: true, composed: true }) snapSelectMenuSearchUpdated: EventEmitter<string>

  @State() isOpen = false
  @State() selectedItem = null
  @State() lastChanged = new Date().getTime()
  @State() localSelectMenuOptions: SnapSelectMenuOption[] = []
  @State() filteredLocalSelectMenuOptions: SnapSelectMenuOption[] = []
  @State() itemSelected = 0
  @State() isMobile: boolean
  @State() searchWord: ''

  private mobileWidth = 769
  private textInput!: HTMLInputElement

  @Watch('selectMenuOptions')
  onselectMenuOptionsChanged(value: SnapSelectMenuOption[]) {
    this.localSelectMenuOptions = [...value]
    this.filteredLocalSelectMenuOptions = [...value]

    if (this.float && this.multi) {
      this.sortMenuOptions()
    }

    this.getSelectedItems()

    if (this.searchWord) this.onInput()
  }

  @Listen('click', { target: 'window' })
  checkForClickOutside(e: any) {
    const now = new Date().getTime()

    if (!!this.isOpen && !this.element.contains(e.target) && now - this.lastChanged > 500) {
      this.toggleDropdown()
    }
  }

  @Listen('resize', { target: 'window' })
  checkScreen() {
    this.isMobile = document.body.clientWidth < this.mobileWidth
  }

  @Method()
  async toggleDropdown() {
    if (this.disabled || this.locked) return

    this.lastChanged = new Date().getTime()

    if (!this.isOpen && this.float && this.multi) {
      this.floatSelectedOptionsTop()
      this.getSelectedItems()
    }

    this.isOpen = !this.isOpen
  }

  componentWillLoad() {
    this.onselectMenuOptionsChanged(this.selectMenuOptions)
    this.checkScreen()
  }

  sortMenuOptions() {
    this.filteredLocalSelectMenuOptions.sort((a, b) => {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()

      if (b.selectAll) {
        return 1
      }
      else if (nameA < nameB) {
        return -1
      }
      else if (nameA > nameB) {
        return 1
      }
      else {
        return 0
      }
    })
  }

  singleButtonClick(name) {
    this.filteredLocalSelectMenuOptions = [
      ...this.filteredLocalSelectMenuOptions.map(opt => {
        return {
          ...opt,
          selected: opt.name.toLowerCase() === name.toLowerCase() ? !opt.selected : false,
        }
      }),
    ]

    this.localSelectMenuOptions = [
      ...this.localSelectMenuOptions.map(opt => {
        return {
          ...opt,
          selected: opt.name.toLowerCase() === name.toLowerCase() ? !opt.selected : false,
        }
      }),
    ]

    this.selectedItem = name

    this.toggleDropdown()
    this.publishUpdate()
  }

  multiButtonClick(option) {
    if (this.filteredLocalSelectMenuOptions[0].selectAll && option.selected === true && this.filteredLocalSelectMenuOptions[0].name !== option.name) {
      this.filteredLocalSelectMenuOptions[0].selected = false
    }

    if (option.selectAll) {
      this.filteredLocalSelectMenuOptions = [
        ...this.filteredLocalSelectMenuOptions.map(opt => {
          return {
            ...opt,
            selected: !option.selected
          }
        })
      ]
    }
    else {
      this.filteredLocalSelectMenuOptions = [
        ...this.filteredLocalSelectMenuOptions.map(opt => {
          return {
            ...opt,
            selected: opt.name.toLowerCase() === option.name.toLowerCase() ? !opt.selected : opt.selected,
          }
        }),
      ]
    }

    this.publishUpdate()
  }

  getSelectedItems() {
    this.itemSelected = 0
    const searchResult = this.filteredLocalSelectMenuOptions.some(val => {
      if (val.selected) {
        this.selectedItem = val.name
        return true
      }
    })

    if (!searchResult) {
      this.selectedItem = null
    }

    this.filteredLocalSelectMenuOptions.map(opt => {
      if (opt.selected && !opt.selectAll) {
        this.itemSelected ++
      }
    })
  }

  publishUpdate() {
    if (!this.multi) {
      this.snapSelectMenuUpdated.emit(this.localSelectMenuOptions)
      return
    }

    const tempLocalSelectMenuOptions = [...this.localSelectMenuOptions]
    tempLocalSelectMenuOptions.forEach(opt => {
      const option = this.filteredLocalSelectMenuOptions.find(el => el.name === opt.name)
      if (option) {
        opt.selected = option.selected
      }
    })
    this.localSelectMenuOptions = [...tempLocalSelectMenuOptions]
    this.snapSelectMenuUpdated.emit(this.localSelectMenuOptions)

    this.getSelectedItems()
  }

  getClasses() {
    const classes = [`select-menu-btn`]
    this.error ? classes.push('select-menu-btn--error') : null
    this.disabled ? classes.push('select-menu-btn--disabled') : null
    this.locked ? classes.push('select-menu-btn--locked') : null
    return classes.join(' ').toString()
  }

  onInput(event?) {
    const htmlRegexG = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g
    if (event) {
      this.searchWord = event.target.value
    }


    this.searchMethod === 'start' ?
      this.filteredLocalSelectMenuOptions = [...this.localSelectMenuOptions.filter(item => {
        return  item.label
          ? item.label.toLowerCase().replace(htmlRegexG, '').startsWith(this.searchWord.toLowerCase())
          : item.name.toLowerCase().startsWith(this.searchWord.toLowerCase())
      })]
      :
      this.filteredLocalSelectMenuOptions = [...this.localSelectMenuOptions.filter(item => {
        return item.label
        ? item.label.toLowerCase().includes(this.searchWord.toLowerCase())
        : item.name.toLowerCase().includes(this.searchWord.toLowerCase())
      })]

    this.snapSelectMenuSearchUpdated.emit(this.searchWord)
  }

  clearSearchField() {
    if (!this.multi) {
      this.textInput.value = ''
      this.searchWord = ''
      this.filteredLocalSelectMenuOptions = [...this.localSelectMenuOptions]
    }
    else {
      this.textInput.value = ''
      this.searchWord = ''
      const tempLocalSelectMenuOptions = [...this.localSelectMenuOptions]
      tempLocalSelectMenuOptions.forEach(opt => {
        const option = this.filteredLocalSelectMenuOptions.find(el => el.name === opt.name)
        if (option) {
          opt.selected = option.selected
        }
      })
      this.filteredLocalSelectMenuOptions = [...tempLocalSelectMenuOptions]
    }
  }

  floatSelectedOptionsTop() {
    const selectedOptions = this.filteredLocalSelectMenuOptions.filter(option => option.selected)
    const nonSelectedOptions = this.filteredLocalSelectMenuOptions.filter(option => !option.selected)

    selectedOptions.sort((a, b) => {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()
      if (nameA < nameB) return -1
      if (nameA > nameB) return 1
      return 0
    })

    nonSelectedOptions.sort((a, b) => {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()
      if (nameA < nameB) return -1
      if (nameA > nameB) return 1
      return 0
    })

    this.filteredLocalSelectMenuOptions = selectedOptions.concat(nonSelectedOptions)
    this.filteredLocalSelectMenuOptions.sort((a, b) => {

      if (a.selectAll === b.selectAll) {
        return 0
      }

      if (a.selectAll) {
        return -1
      }

      return 1
    })

  }

  renderNoResult() {
    return (
      <div class="no-result-container">
        <p class="no-result-title">Sorry, We Couldn’t Find Any Results</p>
        <p class="no-result-body">Try double checking your search for any typos or spelling errors - or try a
          different search term.</p>
      </div>
    )
  }

  renderSearchField() {
    return (
        <div class="snap-select-menu-search">
          {!this.searchWord && <snap-icon class="snap-select-menu-icon-search" icon="search-solid" color="#94A3B8" size="sm"></snap-icon>}

          <input
            type="text"
            class={`snap-select-search-field ${this.searchWord ? 'active' : ''}`}
            placeholder="Search"
            autoComplete="off"
            tabindex="-1"
            value={this.searchWord}
            ref={(el) => this.textInput = el as HTMLInputElement}
            onInput={(event) => {this.onInput(event)}}
          />

          {this.searchWord &&
            <snap-icon
              class="snap-select-menu-icon-close"
              icon="x-line"
              color="#64748B"
              size="sm"
              onClick = {() => {this.clearSearchField()}}
              >
            </snap-icon>
            }
        </div>
    )
  }

  dropdownListScrollHandler(e) {
    const elementStyles = getComputedStyle(e.target)
    const borderY = parseFloat(elementStyles.borderTopWidth) + parseFloat(elementStyles.borderBottomWidth)
    const bottom = e.target.scrollTop + e.target.offsetHeight - borderY >= e.target.scrollHeight
    if (bottom) {
      this.snapSelectMenuScrollUpdated.emit(bottom)
    }
  }

  render() {
    return (
      <Host class="snap-select-menu">
        <div class="select-menu-btn-wrapper">
          { !!this.cornerHint && !!this.label &&
            <div class="corner-hint-wrapper">
              <label class="label">
                { this.label }
                { this.required && <span class="required">*</span> }
              </label>
              <div class="corner-hint">{this.cornerHint}</div>
            </div>
          }

          { !!this.label && !this.cornerHint &&
            <label class="label">
              { this.label }
              { this.required && <span class="required">*</span> }
            </label>
          }

          { !!this.cornerHint && !this.label &&
            <div class="corner-hint-wrapper corner-hint-wrapper--right">
              <div class="corner-hint">{this.cornerHint}</div>
            </div>
          }

          <button type="button" class={this.getClasses()} onClick={() => this.toggleDropdown()}>
            <snap class="btn-selected-name">
              {this.selectedItem !== null &&
                <span class="has-value">{this.selectedItem}</span>
              }
              {this.itemSelected > 1 &&
                <snap-indicator class="snap-indicator-gray" size="sm" text={`+${this.itemSelected - 1}`}></snap-indicator>
              }
              {this.placeholder && this.selectedItem === null &&
                <span>{this.placeholder}</span>
              }
            </snap>
            <div class="menu-btn-right">
              { !this.multi && <snap-icon class="chevron-icon" icon="chevron-down-solid" size="sm"></snap-icon> }
              { this.multi && <snap-icon class="chevron-icon" icon="selector-solid" size="sm"></snap-icon> }

              { this.error && <snap-icon class="error-icon" icon="exclamation-circle-solid" color="#EF4444" size="sm"></snap-icon> }
              { this.locked && <snap-icon class="lock-closed-icon" icon="lock-closed-solid" color="#94A3B8" size="sm"></snap-icon> }
            </div>
          </button>

          { !!this.helpText && !this.isOpen && <div class="help-text"><p>{this.helpText}</p></div> }

          { this.multi ?
            <div>
              <ul
                class={`dropdown-list ${this.isOpen ? 'open' : ''} ${this.relative ? 'relative' : ''}`}
                onScroll={(e) => this.dropdownListScrollHandler(e)}
                style={{'max-height' : this.dropdownHeight ? `${this.dropdownHeight}px` : null}}
              >
                {this.search && this.renderSearchField()}
                {this.filteredLocalSelectMenuOptions.map(option => (
                  <li class={`dropdown-item ${option.selected ? 'active' : ''}`} onClick={() => {
                    this.multiButtonClick(option)
                  }}>
                    <snap-checkbox-button checked={option.selected} no-focus={true}></snap-checkbox-button>
                    {option.avatar && <img src={option.avatar} alt="avatar" class="dropdown-item-avatar" />}
                    { option.label ?
                      <span innerHTML={option.label}></span>
                      :
                      <span>{option.name}</span>
                    }
                  </li>
                ))}
                {this.filteredLocalSelectMenuOptions.length === 0 && this.renderNoResult()}
              </ul>

              {this.modalType === 'fullscreen' && this.isMobile &&
                <div class={`dropdown-list-modal ${this.isOpen ? 'open' : ''}`}>
                  <div class="dropdown-list-modal-backdrop"></div>
                  <div class="dropdown-list-modal-container">
                    <div class="dropdown-list-modal-header">
                      <div class="modal-header-title">{this.modalTitle}</div>
                      <snap-icon class="modal-header-close" icon="x-line" color="#64748B" size="md"
                                 onClick={() => this.isOpen = false}></snap-icon>
                    </div>
                    <div class="dropdown-list-modal-body">
                      {this.search && this.renderSearchField()}
                      <ul class={`modal-list ${this.isOpen ? 'open' : ''}`}>
                        {this.filteredLocalSelectMenuOptions.map(option => (
                          <li class={`modal-list-item ${option.selected ? 'active' : ''}`} onClick={() => {
                            this.multiButtonClick(option)
                          }}>
                            <snap-checkbox-button checked={option.selected} no-focus={true}></snap-checkbox-button>
                            {option.avatar && <img src={option.avatar} alt="avatar" class="dropdown-item-avatar" />}
                            { option.label ?
                              <span innerHTML={option.label}></span>
                              :
                              <span>{option.name}</span>
                            }
                          </li>
                        ))}
                        {this.filteredLocalSelectMenuOptions.length === 0 && this.renderNoResult()}
                      </ul>
                    </div>
                  </div>
                </div>
              }
            </div>
            :
            // Single choose
            <div>
              <ul
                class={`dropdown-list ${this.isOpen ? 'open' : ''}`}
                onScroll={(e) => this.dropdownListScrollHandler(e)}
                style={{'max-height' : this.dropdownHeight ? `${this.dropdownHeight}px` : null}}
              >
                {this.search && this.renderSearchField()}
                {this.filteredLocalSelectMenuOptions.map(option => (
                  <li class={`dropdown-item ${option.selected ? 'active' : ''}`} onClick={() => {
                    this.singleButtonClick(option.name)
                  }}>
                    {option.avatar && <img src={option.avatar} alt="avatar" class="dropdown-item-avatar" />}
                    { option.label ?
                      <span innerHTML={option.label}></span>
                      :
                      <span>{option.name}</span>
                    }
                  </li>
                ))}
                {this.filteredLocalSelectMenuOptions.length === 0 && this.renderNoResult()}
              </ul>

              {this.modalType === 'fullscreen' && this.isMobile &&
                <div class={`dropdown-list-modal ${this.isOpen ? 'open' : ''}`}>
                  <div class="dropdown-list-modal-backdrop"></div>
                  <div class="dropdown-list-modal-container">
                    <div class="dropdown-list-modal-header">
                      <div class="modal-header-title">{this.modalTitle}</div>
                      <snap-icon class="modal-header-close" icon="x-line" color="#64748B" size="md"
                                 onClick={() => this.isOpen = false}></snap-icon>
                    </div>
                    <div class="dropdown-list-modal-body">
                      {this.search && this.renderSearchField()}
                      <ul class={`modal-list ${this.isOpen ? 'open' : ''}`}>
                        {this.filteredLocalSelectMenuOptions.map(option => (
                          <li class={`modal-list-item ${option.selected ? 'active' : ''}`} onClick={() => {
                            this.singleButtonClick(option.name)
                          }}>
                            {option.avatar && <img src={option.avatar} alt="avatar" class="dropdown-item-avatar" />}
                            { option.label ?
                              <span innerHTML={option.label}></span>
                              :
                              <span>{option.name}</span>
                            }
                          </li>
                        ))}
                        {this.filteredLocalSelectMenuOptions.length === 0 && this.renderNoResult()}
                      </ul>
                    </div>
                  </div>
                </div>
              }
            </div>
          }

          {!!this.isOpen && !!this.isMobile && this.modalType === 'drawer' && (
            <snap-action-sheet
              onSnap-action-sheet-close={() => {this.isOpen = false}}
              header={this.modalTitle}>
              <ul class={`modal-list ${this.isOpen ? 'open' : ''}`}>
                {this.search && this.renderSearchField()}
                {this.filteredLocalSelectMenuOptions.map(option => (
                  <li class={`modal-list-item ${option.selected ? 'active' : ''}`} onClick={() => {
                    { this.multi ?
                      this.multiButtonClick(option)
                      :
                      this.singleButtonClick(option.name)
                    }
                  }}>
                    { this.multi && <snap-checkbox-button checked={option.selected} no-focus={true}></snap-checkbox-button> }
                    {option.avatar && <img src={option.avatar} alt="avatar" class="dropdown-item-avatar" />}
                    { option.label ?
                      <span innerHTML={option.label}></span>
                      :
                      <span>{option.name}</span>
                    }
                  </li>
                ))}
                {this.filteredLocalSelectMenuOptions.length === 0 && this.renderNoResult()}
              </ul>
            </snap-action-sheet>
          )}
        </div>
      </Host>
    )
  }
}
